/**
 * Vergleich von Einheiten-Bezeichnungen, die als Freitext in mehreren Tabellen
 * liegen (`zaehler.einheit_bezeichnung`, `vertraege.einheit`,
 * `objekt_einheiten.bezeichnung`) und dort uneinheitlich erfasst sind — "W1" vs.
 * "w1", "WE 12" vs. "we12", mal mit, mal ohne Leerzeichen. Ein exakter Vergleich
 * trifft gegen echte Stammdaten kaum je, deshalb wird toleranter verglichen:
 * Groß-/Kleinschreibung ist egal, und ob ein Leerzeichen zwischen Buchstaben und
 * Zahl steht ("W 36" vs. "w36") auch — solange das Leerzeichen nur auf EINER
 * Seite steht (siehe passtZurEinheit).
 *
 * Bewusst NICHT entfernt werden Satzzeichen oder sonstige Zeichen — "W1" und
 * "W12" bleiben unterschiedlich. Auch innere Leerzeichen werden nicht blind
 * entfernt (s.u.), keine inhaltliche Fuzzy-Suche.
 *
 * Fix-Runde 1 (Review gegen Produktionsdaten): Das ursprüngliche
 * normalisiereBezeichnung() entfernte ALLE Leerzeichen. Damit verschwinden
 * Wortgrenzen — "10 1" und "1 01" wurden beide zu "101" und galten fälschlich
 * als dieselbe Einheit. Das Bestandsmuster für Bezeichnungen sieht Tokens vor
 * ("010 W10 1 OG links"), daher jetzt: Ränder trimmen, aber innere
 * Leerzeichenfolgen nur auf ein einzelnes Leerzeichen zusammenfassen (nicht
 * entfernen). Das Ignorieren eines einzelnen inneren Leerzeichens ("W 36" vs.
 * "w36") übernimmt passtZurEinheit() gezielt nur dann, wenn es eindeutig ist.
 *
 * Bekannter Restfall (unverändert, keine Verschlechterung): führende Nullen
 * und Trennzeichen werden nicht angeglichen — "GW01"/"GW1" sowie
 * "G01"/"G01.1" bleiben unterschiedlich und finden hier nicht zusammen.
 */

/**
 * Normalisiert eine Einheiten-Bezeichnung für den Vergleich: trimmt die
 * Ränder, senkt auf Kleinschreibung und fasst innere Leerzeichenfolgen zu
 * einem einzelnen Leerzeichen zusammen (entfernt sie NICHT — das würde
 * Tokengrenzen verschieben, z.B. "10 1" und "1 01" zu "101"). `null` ergibt
 * einen leeren Text.
 *
 * @param v - Rohe Bezeichnung (oder null)
 * @returns Normalisierte Bezeichnung, ggf. leerer Text
 */
export function normalisiereBezeichnung(v: string | null): string {
  return (v ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Prüft, ob zwei Einheiten-Bezeichnungen dieselbe Einheit meinen — nach
 * Normalisierung (Groß-/Kleinschreibung, äußere Leerzeichen, mehrfache innere
 * Leerzeichen). Leere Werte gelten immer als nicht zugeordnet, auch wenn beide
 * leer sind (sonst würde ein fehlender Wert auf beiden Seiten fälschlich als
 * Treffer gelten).
 *
 * Stimmen die normalisierten Texte nicht exakt überein, wird zusätzlich ein
 * Vergleich ohne innere Leerzeichen versucht — aber NUR, wenn genau eine der
 * beiden Seiten ein inneres Leerzeichen enthält ("W 36" vs. "w36"). Tragen
 * BEIDE Seiten ein inneres Leerzeichen, könnten das unterschiedliche
 * Tokengrenzen sein ("10 1" vs. "1 01") — dann wird das Leerzeichen nicht
 * ignoriert, damit unterschiedliche Einheiten nicht zusammenfallen.
 *
 * @param a - Erste Bezeichnung (oder null)
 * @param b - Zweite Bezeichnung (oder null)
 * @returns true, wenn beide Bezeichnungen dieselbe Einheit meinen
 */
export function passtZurEinheit(a: string | null, b: string | null): boolean {
  const na = normalisiereBezeichnung(a);
  const nb = normalisiereBezeichnung(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  const aHatLeerzeichen = na.includes(' ');
  const bHatLeerzeichen = nb.includes(' ');
  if (aHatLeerzeichen === bHatLeerzeichen) return false;
  return na.replace(/ /g, '') === nb.replace(/ /g, '');
}
